'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { type MouseEventHandler, useState } from 'react';
import IconLikeBordered from '@/components/assets/icon_like_bordered.svg';
import IconLikeFilled from '@/components/assets/icon_like_filled.svg';
import VerifiedBadge from '@/components/VerifiedBadge';
import { NEW_TAB_LINK_PROPS } from '@/constants/card';
import { useWorldCardContext } from '@/context/ViverseWorldCardContext';
import { useLikedRoom } from '@/hooks/useLikedRoom';
import { Link } from '@/i18n/navigation';
import { hasVerifiedCreatorBadge } from '@/lib/cms';
import { cn } from '@/lib/utils';
const MoreAction = dynamic(() => import('@/components/MoreAction'), {
  ssr: false
});
export default function ViverseWorldCardInfo() {
  const $ = _c(47);
  const {
    targetUrl,
    cardType,
    noAccess,
    customDesc,
    hideLiked,
    isCoOwnedRoom,
    showMoreAction,
    shareOnly,
    stopSetAsFeatured,
    moreActionContent,
    onAfterMutatingWorld,
    isClient,
    info,
    href,
    openLinksInNewTab
  } = useWorldCardContext();
  const {
    id,
    title,
    hubs_url,
    owner,
    is_liked,
    is_voucher_room,
    view_count
  } = info;
  const {
    isLiked,
    handleClickLike
  } = useLikedRoom(id || "", href || hubs_url || "/", is_liked);
  const [likeAnimationKey, setLikeAnimationKey] = useState(0);
  const hasActions = isClient && (!hideLiked || showMoreAction && !!id);
  let t0;
  if ($[0] !== handleClickLike) {
    t0 = event => {
      setLikeAnimationKey(_temp);
      handleClickLike(event);
    };
    $[0] = handleClickLike;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleAnimatedLikeClick = t0;
  const t1 = openLinksInNewTab && NEW_TAB_LINK_PROPS;
  let t2;
  if ($[2] !== title) {
    t2 = <span className="line-clamp-1 text-[14px] leading-[1.3] font-semibold text-[#eeeef0]">{title}</span>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== targetUrl) {
    t3 = <Link href={targetUrl} prefetch={false} className="-my-1 flex min-h-6 items-center overflow-hidden py-1" draggable={false} {...t1}>{t2}</Link>;
    $[4] = t1;
    $[5] = t2;
    $[6] = targetUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleAnimatedLikeClick || $[9] !== hasActions || $[10] !== hideLiked || $[11] !== id || $[12] !== info || $[13] !== isCoOwnedRoom || $[14] !== isLiked || $[15] !== is_voucher_room || $[16] !== likeAnimationKey || $[17] !== moreActionContent || $[18] !== onAfterMutatingWorld || $[19] !== shareOnly || $[20] !== showMoreAction || $[21] !== stopSetAsFeatured) {
    t4 = hasActions && <div className="flex h-0 shrink-0 items-center gap-1 overflow-visible">{!hideLiked && <button type="button" className={cn("focus-visible:ring-vive-border-focus focus-visible:ring-offset-background hover:ring-0.5 relative flex size-7 cursor-pointer items-center justify-center rounded-full text-white/90 opacity-0 transition-[opacity,transform,background-color,color,box-shadow,backdrop-filter] duration-300 ease-out outline-none group-hover:opacity-100 hover:bg-white/10 hover:text-white hover:shadow-[0_10px_24px_rgba(18,18,22,0.28)] hover:ring-white/20 hover:backdrop-blur-md focus-visible:bg-white/10 focus-visible:text-white focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:backdrop-blur-md active:scale-95 active:bg-white/15", isLiked && "text-vive-brand-40 hover:text-vive-brand-40 focus-visible:text-vive-brand-40 opacity-100")} aria-label={isLiked ? "Unlike world" : "Like world"} aria-pressed={isLiked} onClick={handleAnimatedLikeClick}><span key={`like-icon-${likeAnimationKey}`} className={cn("relative z-10 flex size-5 items-center justify-center", likeAnimationKey > 0 && "motion-safe:animate-[like-icon-pop_420ms_cubic-bezier(0.2,1.4,0.28,1)_both] motion-reduce:animate-none")}>{isLiked ? <IconLikeFilled className="size-5 p-0.5" /> : <IconLikeBordered className="size-5 p-0.5" />}</span></button>}{showMoreAction && id && <div className="flex items-center"><MoreAction info={info} shareOnly={shareOnly} isCoOwnedRoom={isCoOwnedRoom} onAfterMutatingWorld={onAfterMutatingWorld} stopSetAsFeatured={stopSetAsFeatured} isPremiumRoom={!!is_voucher_room} triggerClassName="focus-visible:ring-vive-border-focus focus-visible:ring-offset-background hover:ring-0.5 relative flex size-7 cursor-pointer items-center justify-center rounded-full text-white/90 opacity-0 transition-[opacity,transform,background-color,color,box-shadow,backdrop-filter] duration-300 ease-out outline-none group-hover:opacity-100 hover:bg-white/10 hover:text-white hover:shadow-[0_10px_24px_rgba(18,18,22,0.28)] hover:ring-white/20 hover:backdrop-blur-md hover:[&_path]:fill-white focus-visible:bg-white/10 focus-visible:text-white focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:backdrop-blur-md focus-visible:[&_path]:fill-white active:scale-95 active:bg-white/15 [&_path]:fill-white/90 [&_path]:transition-colors [&_path]:duration-300 [&_path]:ease-out" iconClassName="size-5 p-0.5 transition-colors duration-300">{moreActionContent}</MoreAction></div>}</div>;
    $[8] = handleAnimatedLikeClick;
    $[9] = hasActions;
    $[10] = hideLiked;
    $[11] = id;
    $[12] = info;
    $[13] = isCoOwnedRoom;
    $[14] = isLiked;
    $[15] = is_voucher_room;
    $[16] = likeAnimationKey;
    $[17] = moreActionContent;
    $[18] = onAfterMutatingWorld;
    $[19] = shareOnly;
    $[20] = showMoreAction;
    $[21] = stopSetAsFeatured;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t3 || $[24] !== t4) {
    t5 = <div className="flex items-center justify-between gap-2">{t3}{t4}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const t6 = `/profile/${owner?.community_user_id}`;
  let t7;
  if ($[26] !== noAccess) {
    t7 = cn("text-vive-primary-30 flex min-h-6 max-w-full items-center gap-1 self-start overflow-hidden text-[12px] leading-[1.3] tracking-[0.15px] whitespace-nowrap transition-colors duration-300 hover:text-white hover:underline", {
      invisible: noAccess
    });
    $[26] = noAccess;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const t8 = openLinksInNewTab && NEW_TAB_LINK_PROPS;
  const t9 = customDesc ?? owner?.display_name;
  let t10;
  if ($[28] !== t9) {
    t10 = <span className="truncate">{t9}</span>;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== owner) {
    t11 = hasVerifiedCreatorBadge(owner);
    $[30] = owner;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11) {
    t12 = <VerifiedBadge isVerified={t11} />;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t12 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t13 = <Link href={t6} prefetch={false} className={t7} draggable={false} {...t8}>{t10}{t12}</Link>;
    $[34] = t10;
    $[35] = t12;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== cardType || $[41] !== view_count) {
    t14 = cardType === "videos" && view_count !== undefined && <p className="text-vive-primary-30 text-[12px] tracking-[0.15px]">{view_count.toLocaleString()} views</p>;
    $[40] = cardType;
    $[41] = view_count;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t5) {
    t15 = <div className="flex flex-col gap-0.5 px-0.5 pt-1">{t5}{t13}{t14}</div>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t5;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
}
function _temp(k) {
  return k + 1;
}